import React, { useState, useEffect, useMemo } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../api";
import NotificationBell from "./NotificationBell";
import ApprovalsInboxPanel from "./ApprovalsInboxPanel";
import { COMPANIES, COMPANY_LIST } from "../companies";
import { buildNav, accessibleItems } from "../nav";
import { usePendingApprovals } from "../hooks/usePendingApprovals";
import {
  LogOut, ChevronDown, ShieldCheck, Menu, X, Check, HelpCircle, PlayCircle, Search,
} from "lucide-react";

const guideTarget = (pathname, isAdmin) => {
  const p = pathname || "/";
  const s = (a, g) => ({ a, g });
  if (p === "/") return s("dipendente", "Home e Profilo");
  if (p.startsWith("/ferie")) return s("dipendente", "Ferie e Permessi");
  if (p.startsWith("/presenze")) return s("dipendente", "Presenze");
  if (p.startsWith("/trasferte") || p.startsWith("/straordinari") || p.startsWith("/congedi") || p.startsWith("/smart-working")) return s("dipendente", "Trasferte, straordinari, congedi e smart working");
  if (p.startsWith("/calendario") || p.startsWith("/rubrica") || p.startsWith("/organigramma") || p.startsWith("/comunicazioni")) return s("dipendente", "Calendario, rubrica e organigramma");
  if (p.startsWith("/buste-paga") || p.startsWith("/documenti") || p.startsWith("/certificati") || p.startsWith("/formazione")) return s("dipendente", "Buste paga, documenti e certificati");
  if (p.startsWith("/profilo")) return s("dipendente", "Home e Profilo");
  if (p.startsWith("/welfare")) return isAdmin ? s("hr", "Beni aziendali (Auto e Dotazioni)") : s("dipendente", "Welfare e beni assegnati");
  if (p.startsWith("/approvazioni")) return s("hr", "Approvazioni");
  if (p.startsWith("/report")) return s("hr", "Report e statistiche");
  if (p.startsWith("/admin")) return s("hr", "Gestione HR");
  return s(isAdmin ? "hr" : "dipendente", "");
};

const ROLE_LABEL = { admin: "Amministratore HR", responsabile: "Responsabile", dipendente: "Dipendente" };

const slugifyGuide = (s) => "guide-grp-" + (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

const IconChip = ({ item, size = 18 }) => (
  <span className="w-9 h-9 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: `${item.color}1a`, color: item.color }}>
    <item.icon size={size} />
  </span>
);

const PendingBadge = ({ count, testid, className = "" }) =>
  count > 0 ? (
    <span data-testid={testid} className={`inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#e30613] text-white text-[10px] font-black leading-none ${className}`}>
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

// Ricerca globale: salto rapido a qualsiasi funzione del portale digitandone il nome.
const GlobalSearch = ({ items, onGo, testidPrefix = "global-search" }) => {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const norm = (s) => (s || "").toLowerCase();
  const results = q.trim()
    ? items.filter((it) => norm(it.label).includes(norm(q)) || norm(it.desc).includes(norm(q))).slice(0, 8)
    : [];
  const choose = (it) => { setQ(""); setOpen(false); onGo(it); };
  return (
    <div className="relative w-full" data-testid={testidPrefix}>
      <div className="flex items-center gap-2 bg-gray-100 border border-gray-200 rounded-full px-3.5 h-9 focus-within:border-[#16345c] focus-within:bg-white transition-colors">
        <Search size={16} className="text-gray-400 shrink-0" />
        <input
          data-testid={`${testidPrefix}-input`}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 150)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && results[0]) choose(results[0]);
            if (e.key === "Escape") { setQ(""); setOpen(false); }
          }}
          placeholder="Cerca una funzione..."
          className="flex-1 bg-transparent text-[13px] text-gray-700 outline-none placeholder:text-gray-400 min-w-0"
        />
        {q && (
          <button onMouseDown={(e) => e.preventDefault()} onClick={() => { setQ(""); setOpen(false); }} data-testid={`${testidPrefix}-clear`} className="text-gray-400 hover:text-gray-600 shrink-0" aria-label="Cancella ricerca">
            <X size={14} />
          </button>
        )}
      </div>
      {open && q.trim() && (
        <div data-testid={`${testidPrefix}-results`} className="absolute left-0 right-0 top-full mt-1.5 bg-white border border-gray-200 shadow-xl rounded-lg py-1.5 z-[70] max-h-[60vh] overflow-y-auto">
          {results.length === 0 ? (
            <div data-testid={`${testidPrefix}-empty`} className="px-4 py-3 text-[13px] text-gray-400">Nessuna funzione trovata</div>
          ) : results.map((it) => (
            <button key={it.key} data-testid={`${testidPrefix}-item-${it.key}`} onMouseDown={(e) => e.preventDefault()} onClick={() => choose(it)}
              className="w-full flex items-center gap-3 px-3 py-2 text-left hover:bg-[#f0f2f5] transition-colors">
              <IconChip item={it} size={16} />
              <span className="min-w-0">
                <span className="block text-[13px] font-bold text-[#16345c] truncate">{it.label}</span>
                {it.desc && <span className="block text-[11px] text-gray-500 truncate">{it.desc}</span>}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const HRHeader = () => {
  const { user, logout, isAdmin, isApprover, isExternal, isEmployee, effectiveBrand, setBrandCompany } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const [brandOpen, setBrandOpen] = useState(false);
  const [openMacro, setOpenMacro] = useState(null);
  const [openAcc, setOpenAcc] = useState({});
  const [hasGuideNews, setHasGuideNews] = useState(false);
  const brand = COMPANIES[effectiveBrand] || COMPANIES.teleborsa;
  const initials = (user?.name || "U").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  const { quick, macros, extra } = buildNav({ isAdmin, isApprover, isExternal, isEmployee });
  const searchItems = useMemo(() => accessibleItems({ isAdmin, isApprover, isExternal, isEmployee }), [isAdmin, isApprover, isExternal, isEmployee]);
  const { count: pendingApprovals } = usePendingApprovals(isApprover);

  useEffect(() => {
    api.get("/guide").then((r) => {
      const sections = r.data?.sections || [];
      const newSlugs = sections.flatMap((s) => s.groups.filter((g) => g.new).map((g) => slugifyGuide(g.title)));
      let read = [];
      try { read = JSON.parse(localStorage.getItem("tb_guide_read") || "[]"); } catch { read = []; }
      setHasGuideNews(newSlugs.some((sl) => !read.includes(sl)));
    }).catch(() => {});
  }, [location.pathname]);

  useEffect(() => { setOpenMacro(null); setMobileNav(false); }, [location.pathname]);

  const isItemActive = (it) => it.to && (it.to === "/" ? location.pathname === "/" : location.pathname.startsWith(it.to));
  const isMacroActive = (m) => m.items.some((it) => isItemActive(it));

  const handleLogout = () => { logout(); navigate("/login"); };

  const openContextGuide = () => {
    const t = guideTarget(location.pathname, isAdmin);
    const params = new URLSearchParams({ a: t.a });
    if (t.g) params.set("g", t.g);
    navigate(`/guida?${params.toString()}`);
  };

  const replayTour = () => {
    setOpen(false);
    if (location.pathname === "/") window.dispatchEvent(new Event("tb-replay-tour"));
    else { localStorage.setItem("tb_tour_replay", "1"); navigate("/"); }
  };

  const go = (it) => {
    setOpenMacro(null); setMobileNav(false);
    if (it.external && it.href) window.open(it.href, "_blank", "noopener,noreferrer");
    else if (it.to) navigate(it.to);
  };

  const quickDesktopClass = (it) =>
    `flex items-center gap-1.5 px-3.5 h-full text-[12.5px] font-bold uppercase tracking-wide whitespace-nowrap border-b-[3px] transition-colors focus:outline-none focus:ring-2 focus:ring-[#ff7e00] focus:ring-inset ${
      isItemActive(it) ? "text-white bg-[#0d1b2f]" : "text-gray-200 border-transparent hover:text-white hover:bg-white/10"
    }`;

  return (
    <header className="w-full sticky top-0 z-50 shadow-sm">
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-[1200px] mx-auto flex items-center justify-between px-4 h-16">
          <div className="flex items-center gap-3">
            <button className="md:hidden text-[#16345c] mr-1" onClick={() => setMobileNav(true)} data-testid="mobile-nav-open" aria-label="Apri menu">
              <Menu size={22} />
            </button>
            <NavLink to="/" className="flex items-center gap-2">
              <img src={brand.logo} alt={brand.label} className="h-8 max-w-[120px] sm:max-w-[170px] object-contain" data-testid="header-logo" />
              <span className="text-[13px] font-bold text-white bg-[#ff7e00] px-2 py-0.5 rounded-sm tracking-wide">HR</span>
            </NavLink>
            {isAdmin && (
              <div className="relative hidden sm:block">
                <button onClick={() => setBrandOpen(!brandOpen)} data-testid="header-company-switcher" className="flex items-center gap-1.5 text-[12px] font-bold text-gray-600 border border-gray-200 rounded-full px-3 py-1.5 hover:bg-gray-50 transition-colors">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: brand.accent }} />
                  {brand.short}
                  <ChevronDown size={13} className={`transition-transform ${brandOpen ? "rotate-180" : ""}`} />
                </button>
                {brandOpen && (
                  <div className="absolute left-0 top-full mt-1 bg-white border border-gray-200 shadow-xl rounded-md min-w-[185px] py-1 z-50" onMouseLeave={() => setBrandOpen(false)}>
                    <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wide text-gray-400">Società (branding)</div>
                    {COMPANY_LIST.map((c) => (
                      <button key={c.code} data-testid={`header-company-${c.code}`} onClick={() => { setBrandCompany(c.code); setBrandOpen(false); }}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-[13px] hover:bg-gray-50 transition-colors ${effectiveBrand === c.code ? "font-bold text-[#16345c]" : "text-gray-600"}`}>
                        <span className="w-2 h-2 rounded-full" style={{ backgroundColor: c.accent }} /> {c.label}
                        {effectiveBrand === c.code && <Check size={14} className="ml-auto text-[#009640]" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="hidden lg:flex flex-1 justify-center px-6">
            <div className="w-full max-w-[380px]">
              <GlobalSearch items={searchItems} onGo={go} />
            </div>
          </div>

          <div className="flex items-center gap-3">
            {!location.pathname.startsWith("/guida") && (
              <button onClick={openContextGuide} data-testid="context-help-btn" title="Apri la guida di questa pagina"
                className="relative w-9 h-9 rounded-full flex items-center justify-center text-gray-500 hover:text-[#16345c] hover:bg-gray-100 transition-colors">
                <HelpCircle size={20} />
                {hasGuideNews && <span data-testid="context-help-dot" className="absolute top-1 right-1 w-2.5 h-2.5 bg-[#e30613] border-2 border-white rounded-full" />}
              </button>
            )}
            {isApprover && <ApprovalsInboxPanel />}
            <NotificationBell />
            <div className="relative">
              <button onClick={() => setOpen(!open)} data-testid="header-user-menu" className="flex items-center gap-2 pl-1 pr-2 py-1 rounded-full hover:bg-gray-100 transition-colors">
                <span className="w-9 h-9 rounded-full bg-[#16345c] text-white flex items-center justify-center text-[13px] font-bold">{initials}</span>
                <div className="hidden sm:block text-left leading-tight">
                  <div className="text-[13px] font-bold text-[#16345c]">{user?.name}</div>
                  <div className="text-[11px] text-gray-500 flex items-center gap-1">
                    {isAdmin && <ShieldCheck size={11} className="text-[#009640]" />}{ROLE_LABEL[user?.role] || "Dipendente"}
                  </div>
                </div>
                <ChevronDown size={15} className={`text-gray-500 transition-transform ${open ? "rotate-180" : ""}`} />
              </button>
              {open && (
                <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 shadow-xl rounded-sm min-w-[230px] py-1 z-50" onMouseLeave={() => setOpen(false)}>
                  <div className="px-4 py-3 border-b border-gray-100">
                    <div className="text-[13px] font-bold text-[#16345c]">{user?.name}</div>
                    <div className="text-[11px] text-gray-500">Matricola {user?.matricola} · {user?.dept}</div>
                  </div>
                  <button onClick={() => { setOpen(false); navigate("/profilo"); }} data-testid="header-user-profile" className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-gray-700 font-semibold hover:bg-gray-50 transition-colors">
                    <ShieldCheck size={15} className="text-[#16345c]" /> Profilo e impostazioni
                  </button>
                  <button onClick={replayTour} data-testid="replay-tour-btn" className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-gray-700 font-semibold hover:bg-gray-50 transition-colors">
                    <PlayCircle size={15} className="text-[#16345c]" /> Rivedi il tour
                  </button>
                  <button onClick={handleLogout} data-testid="header-user-logout" className="w-full flex items-center gap-2 px-4 py-2.5 text-[13px] text-[#c00510] font-semibold hover:bg-red-50 transition-colors">
                    <LogOut size={15} /> Esci
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Barra di navigazione desktop: accessi rapidi + macro-menù */}
      <nav className="bg-[#16345c] hidden md:block relative">
        <div className="max-w-[1200px] mx-auto flex items-center px-2 h-12">
          {/* Accessi rapidi (per ruolo) */}
          <div className="flex items-center h-full" data-testid="nav-quick-access">
            {quick.map((it) => (
              <NavLink key={it.key} to={it.to} className={quickDesktopClass(it)}
                style={isItemActive(it) ? { borderBottomColor: brand.accent } : undefined} data-testid={`nav-quick-${it.key}`}>
                <it.icon size={15} /> {it.label}
                {it.key === "approvazioni" && <PendingBadge count={pendingApprovals} testid="nav-quick-approvazioni-badge" className="ml-1" />}
              </NavLink>
            ))}
          </div>

          <div className="w-px h-6 bg-white/20 mx-2" />

          {/* Macro-menù (mega-menu) */}
          <div className="flex items-center h-full gap-0.5" data-testid="nav-macro-menus">
            {macros.map((m) => (
              <div key={m.key} className="relative h-full flex items-center">
                <button
                  onClick={() => setOpenMacro((v) => (v === m.key ? null : m.key))}
                  data-testid={`nav-macro-${m.key}`}
                  className={`flex items-center gap-1.5 px-3 h-full text-[12.5px] font-semibold uppercase tracking-wide transition-colors focus:outline-none focus:ring-2 focus:ring-[#ff7e00] focus:ring-inset ${
                    openMacro === m.key || isMacroActive(m) ? "text-white" : "text-white/80 hover:text-white"
                  }`}>
                  <m.icon size={15} /> {m.label}
                  <ChevronDown size={13} className={`transition-transform ${openMacro === m.key ? "rotate-180" : ""}`} />
                </button>
                {openMacro === m.key && (
                  <div className="absolute top-full left-0 mt-1 w-[540px] max-w-[90vw] bg-white border border-gray-200 shadow-xl rounded-lg p-4 z-50 animate-in fade-in slide-in-from-top-2 duration-200"
                    data-testid={`macro-panel-${m.key}`}>
                    <div className="flex items-center gap-2 mb-3 text-[11px] font-bold uppercase tracking-wider text-gray-400">
                      <m.icon size={14} /> {m.label}
                    </div>
                    <div className="grid grid-cols-2 gap-1.5">
                      {m.items.map((it) => (
                        <button key={it.key} onClick={() => go(it)} data-testid={`macro-item-${it.key}`}
                          className="flex items-center gap-3 p-2 rounded-md hover:bg-[#f0f2f5] text-left transition-colors focus:outline-none focus:ring-2 focus:ring-[#ff7e00]">
                          <IconChip item={it} />
                          <span className="min-w-0 flex-1">
                            <span className="flex items-center gap-1.5 text-[13px] font-bold text-[#16345c] truncate">
                              {it.label}
                              {it.key === "approvazioni" && <PendingBadge count={pendingApprovals} testid="macro-item-approvazioni-badge" />}
                            </span>
                            {it.desc && <span className="block text-[11px] text-gray-500 truncate">{it.desc}</span>}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Extra: guida / novità */}
          <div className="ml-auto flex items-center h-full">
            {extra.map((it) => (
              <NavLink key={it.key} to={it.to} data-testid={`nav-extra-${it.key}`}
                className={({ isActive }) => `flex items-center gap-1.5 px-3 h-full text-[12px] font-semibold uppercase tracking-wide transition-colors ${isActive ? "text-white" : "text-white/70 hover:text-white"}`}>
                <it.icon size={14} /> {it.label}
              </NavLink>
            ))}
          </div>
        </div>
        {openMacro && <div className="fixed inset-0 z-40" onClick={() => setOpenMacro(null)} data-testid="macro-backdrop" />}
      </nav>

      {/* Drawer mobile */}
      {mobileNav && (
        <div className="md:hidden fixed inset-0 z-[60]" data-testid="mobile-drawer">
          <div className="absolute inset-0 bg-black/40 animate-in fade-in duration-200" onClick={() => setMobileNav(false)} />
          <div className="absolute right-0 top-0 h-full w-80 max-w-[90%] bg-white shadow-2xl overflow-y-auto animate-in slide-in-from-right duration-300">
            <div className="flex items-center justify-between px-4 h-16 border-b border-gray-200 bg-[#16345c]">
              <span className="text-white font-bold text-[15px]">Menu</span>
              <button onClick={() => setMobileNav(false)} data-testid="mobile-nav-close" className="text-white/80 hover:text-white"><X size={22} /></button>
            </div>

            <div className="p-3 border-b border-gray-100">
              <GlobalSearch items={searchItems} onGo={go} testidPrefix="global-search-mobile" />
            </div>

            <div className="p-3">
              <div className="text-[11px] font-bold uppercase tracking-wider text-gray-400 mb-2 px-1">Accesso rapido</div>
              <div className="space-y-1">
                {quick.map((it) => (
                  <button key={it.key} onClick={() => go(it)} data-testid={`mnav-quick-${it.key}`}
                    className={`w-full flex items-center gap-3 py-3 px-3 rounded-md text-left border-l-4 transition-colors ${isItemActive(it) ? "bg-[#16345c]/5 border-[#ff7e00]" : "border-transparent hover:bg-gray-50"}`}>
                    <IconChip item={it} />
                    <span className="text-[14px] font-bold text-[#16345c]">{it.label}</span>
                    {it.key === "approvazioni" && <PendingBadge count={pendingApprovals} testid="mnav-quick-approvazioni-badge" className="ml-auto" />}
                  </button>
                ))}
              </div>
            </div>

            {macros.map((m) => (
              <div key={m.key} className="border-t border-gray-200">
                <button onClick={() => setOpenAcc((s) => ({ ...s, [m.key]: !s[m.key] }))} data-testid={`mnav-macro-${m.key}`}
                  className="w-full flex items-center justify-between py-3 px-4 text-[12px] font-bold uppercase tracking-wider text-gray-500">
                  <span className="flex items-center gap-2"><m.icon size={15} /> {m.label}</span>
                  <ChevronDown size={16} className={`transition-transform ${openAcc[m.key] ? "rotate-180" : ""}`} />
                </button>
                {openAcc[m.key] && (
                  <div className="pb-2">
                    {m.items.map((it) => (
                      <button key={it.key} onClick={() => go(it)} data-testid={`mnav-item-${it.key}`}
                        className="w-full flex items-center gap-3 py-2.5 px-4 text-left hover:bg-gray-50 transition-colors">
                        <IconChip item={it} size={16} />
                        <span className="text-[13px] font-semibold text-gray-700">{it.label}</span>
                        {it.key === "approvazioni" && <PendingBadge count={pendingApprovals} testid="mnav-item-approvazioni-badge" className="ml-auto" />}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ))}

            <div className="border-t border-gray-200 py-2">
              {extra.map((it) => (
                <button key={it.key} onClick={() => go(it)} data-testid={`mnav-extra-${it.key}`}
                  className="w-full flex items-center gap-3 py-2.5 px-4 text-left hover:bg-gray-50 transition-colors">
                  <it.icon size={16} className="text-gray-500" />
                  <span className="text-[13px] font-semibold text-gray-700">{it.label}</span>
                </button>
              ))}
              <button onClick={handleLogout} data-testid="mnav-logout" className="w-full flex items-center gap-3 py-2.5 px-4 text-left text-[#c00510] font-semibold hover:bg-red-50 transition-colors">
                <LogOut size={16} /> Esci
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default HRHeader;
